/** @fileoverview Loads and manages a tenant's Fabrik apps and app actions. */

import { useEffect, useState } from 'react';
import {
  createFabrikApp,
  deleteFabrikApp,
  getFabrikApp,
  listFabrikApps,
} from '../services/appsApi';
import type { FabrikAppSummary } from '../types/apps';

export default function useFabrikApps(tenantId: string) {
  const [apps, setApps] = useState<FabrikAppSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<FabrikAppSummary | null>(null);
  const [deletingAppId, setDeletingAppId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [duplicatingAppId, setDuplicatingAppId] = useState<string | null>(null);
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    listFabrikApps(tenantId, controller.signal)
      .then((response) => setApps(response.apps))
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          setError(reason instanceof Error ? reason.message : 'Unable to load your apps.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [tenantId, reloadCount]);

  const retryLoad = () => {
    setLoading(true);
    setError('');
    setReloadCount((count) => count + 1);
  };

  const duplicateApp = async (app: FabrikAppSummary) => {
    if (duplicatingAppId) return;
    setDuplicatingAppId(app.id);
    setError('');
    try {
      const source = await getFabrikApp(tenantId, app.id);
      if (!source.draft_definition) throw new Error('This app has no draft to duplicate.');
      const name = `${source.draft_definition.app.name.slice(0, 75).trimEnd()} copy`;
      const duplicateDefinition = {
        ...source.draft_definition,
        app: { ...source.draft_definition.app, name },
      };
      const duplicate = await createFabrikApp(tenantId, duplicateDefinition);
      const { systemIds, theme } = app;
      setApps((current) => [{ ...duplicate, systemIds, theme }, ...current]);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Unable to duplicate this app.');
    } finally {
      setDuplicatingAppId(null);
    }
  };

  const deleteApp = async () => {
    if (!deleteTarget) return;
    setDeletingAppId(deleteTarget.id);
    setDeleteError('');
    try {
      await deleteFabrikApp(tenantId, deleteTarget.id);
      setApps((current) => current.filter((app) => app.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (reason: unknown) {
      setDeleteError(reason instanceof Error ? reason.message : 'Unable to delete this app.');
    } finally {
      setDeletingAppId(null);
    }
  };

  return {
    apps,
    loading,
    error,
    deleteTarget,
    setDeleteTarget,
    deletingAppId,
    deleteError,
    setDeleteError,
    retryLoad,
    duplicatingAppId,
    duplicateApp,
    deleteApp,
  };
}
